import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import range from 'lodash/range'
import moment from 'moment'
import { buildSnowflakeQueryParams, SnowflakeQuery } from './pickerData'
import type {
  PurchaseMatchRateRecordWire,
  PurchaseMatchRateResponseWire,
  PurchaseMatchRateRow,
} from './purchaseMatchRate.types'

import { api } from './index'

const PER_PAGE = 1000
// A page holds 1,000 location-months, so this covers over 4,000 locations for 12 months
const MAX_PAGES = 50
const PAGES_IN_PARALLEL = 5

type PageResult =
  | { data: PurchaseMatchRateResponseWire }
  | { error: FetchBaseQueryError }

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isPurchaseMatchRateResponse = (
  data: unknown
): data is PurchaseMatchRateResponseWire =>
  isPlainObject(data) &&
  Array.isArray(data.records) &&
  isPlainObject(data.pagination) &&
  typeof data.pagination.total_pages === 'number'

export const toPurchaseMatchRateRow = (
  record: PurchaseMatchRateRecordWire
): PurchaseMatchRateRow => ({
  locationId: record.location_id === null ? null : Number(record.location_id),
  month: moment
    .utc(0)
    .add(Number(record.order_month), 'days')
    .format('YYYY-MM-DD'),
  purchaseCount: Number(record.purchase_count),
  matchedPurchaseCount: Number(record.matched_purchase_count),
})

const inStorePurchasesIn = (months: string[]): SnowflakeQuery => ({
  tableName: 'te_purchase_match_rate',
  fields: [
    'location_id',
    'order_month',
    'purchase_count',
    'matched_purchase_count',
  ],
  // A location has a row per month, so pages ordered by location alone can repeat or skip one
  orderBy: ['location_id', 'order_month'],
  where: [
    { field: 'channel', operator: '=', value: 'instore' },
    { field: 'order_month', operator: 'in', value: months },
  ],
})

export const purchaseMatchRateApi = api.injectEndpoints({
  endpoints: builder => ({
    // snowflake/query pages its rows and can't sum them, so this reads every page
    getPurchaseMatchRate: builder.query<
      PurchaseMatchRateRow[],
      { months: string[] }
    >({
      async queryFn({ months }, _api, _extraOptions, fetchWithBQ) {
        const fetchPage = async (page: number): Promise<PageResult> => {
          const params = buildSnowflakeQueryParams(
            page,
            PER_PAGE,
            inStorePurchasesIn(months)
          )
          const result = await fetchWithBQ(`snowflake/query?${params}`)
          if (result.error) return { error: result.error }
          if (!isPurchaseMatchRateResponse(result.data)) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Unexpected snowflake/query response',
              },
            }
          }
          return { data: result.data }
        }

        const first = await fetchPage(1)
        if ('error' in first) return first
        const { records, pagination } = first.data
        // An empty first page means no data, even if total_pages overstates
        if (records.length === 0) return { data: [] }
        if (pagination.total_pages > MAX_PAGES) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `More than ${MAX_PAGES} pages of purchase data`,
            },
          }
        }

        const pages = [first.data]
        for (
          let start = 2;
          start <= pagination.total_pages;
          start += PAGES_IN_PARALLEL
        ) {
          const end = Math.min(
            start + PAGES_IN_PARALLEL,
            pagination.total_pages + 1
          )
          const batch = await Promise.all(range(start, end).map(fetchPage))
          for (const page of batch) {
            if ('error' in page) return page
            pages.push(page.data)
          }
        }
        return {
          data: pages.flatMap(page => page.records.map(toPurchaseMatchRateRow)),
        }
      },
    }),
  }),
})

export const { useGetPurchaseMatchRateQuery } = purchaseMatchRateApi
